import { useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useSSE } from './useSSE';
import { apiClient } from '../api/client';
import type {
  ChatMessage,
  ChatMode,
  Citation,
  RawResearchStatus,
  ResearchPhase,
  ResearchStatus,
  ResearchSubQuery,
  SseEvent,
  ThinkingSection,
} from './types';

/**
 * Event emitted by useChat when an edit-specific SSE response arrives that
 * requires touching the Word document. The consumer (useSendMessage) listens
 * and performs the actual Office JS operation.
 */
export interface EditApplyRequest {
  kind: 'full_edit';
  messageId: string;
  s3Key: string;
  editCount?: number;
  /** Server-reported version of the newly-generated DOCX (e.g. "2"). */
  latestVersion?: string;
  /** SHA-256 of the DOCX bytes — plugin persists locally for outdated-copy detection. */
  docxSha256?: string;
}
export interface QuoteEditApplyRequest {
  kind: 'quote_edit';
  messageId: string;
  editedText: string;
  changeId: string;
}
export type ApplyRequest = EditApplyRequest | QuoteEditApplyRequest;

export type LoadedChatType = 'research' | 'word_edit' | 'draft' | null;

export interface UseChatState {
  mode: ChatMode;
  setMode: (m: ChatMode) => void;
  messages: ChatMessage[];
  streamingId: string | null;
  researchStatus: ResearchStatus | null;
  currentThreadId: string | null;
  /** chat_type of the currently-loaded thread. Drives the mode-switch fork guard. */
  loadedChatType: LoadedChatType;
  startResearchThread: () => string;
  startEditThread: () => string;
  /** Explicitly set the current thread (used when resuming existing chat) */
  setCurrentThreadId: (id: string | null) => void;
  appendUserMessage: (text: string) => string;
  appendAiMessage: (patch?: Partial<ChatMessage>) => string;
  updateMessage: (id: string, patch: Partial<ChatMessage>) => void;
  /** Replace messages with a hydrated list (used when resuming a chat from history) */
  loadThread: (threadId: string, messages: ChatMessage[], chatType?: LoadedChatType) => void;
  reset: () => void;
  stop: () => Promise<void>;
  /** Subscribe to apply requests from edit SSE responses */
  onApplyRequest: (handler: (req: ApplyRequest) => void) => () => void;
}

/**
 * Chat state + SSE router. Mirrors frontend-prod's useResearchSSE + useDraftSSE
 * combined behavior. Key invariants:
 *   1. Exactly one AI message slot per user message — thinking updates patch
 *      the existing slot, never create a new one.
 *   2. Events are deduped by (thread_id, feature_id, response hash) so
 *      SSE reconnects or StrictMode-driven double dispatches don't double up.
 *   3. research_status events synthesize thinking sections (Query Decomposition,
 *      Database & Web Search, Verification, Compilation) as phases arrive —
 *      matches web app's setResearchStatus reducer logic.
 *   4. Edit responses (full_edit via s3_key, quote_edit via edited_text) emit
 *      ApplyRequest events that the parent hook handles with Office JS.
 */
export function useChat(): UseChatState {
  const [mode, setMode] = useState<ChatMode>('research');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingId, setStreamingIdState] = useState<string | null>(null);
  const [researchStatus, setResearchStatus] = useState<ResearchStatus | null>(null);
  const [currentThreadId, setCurrentThreadIdState] = useState<string | null>(null);
  // Tracks the chat_type of the loaded thread so TaskPane can detect a
  // mode/type mismatch on send and offer to fork into a new chat.
  const [loadedChatType, setLoadedChatType] = useState<LoadedChatType>(null);

  // Refs so the SSE callback always has the latest values without re-subscribing
  const streamingIdRef = useRef<string | null>(null);
  const currentThreadIdRef = useRef<string | null>(null);
  const researchStatusRef = useRef<ResearchStatus | null>(null);

  const setStreamingId = useCallback((id: string | null) => {
    streamingIdRef.current = id;
    setStreamingIdState(id);
  }, []);

  const setCurrentThreadId = useCallback((id: string | null) => {
    currentThreadIdRef.current = id;
    setCurrentThreadIdState(id);
  }, []);

  const setResearchStatusSafe = useCallback((status: ResearchStatus | null) => {
    researchStatusRef.current = status;
    setResearchStatus(status);
  }, []);

  // Dedup set: keys are (thread_id | feature_id | sub_type | response prefix)
  const seenEventKeysRef = useRef<Set<string>>(new Set());

  // Queue of thinking updates that arrived before the AI message slot existed
  // (shouldn't happen under correct send-ordering, but be defensive).
  const pendingThinkingRef = useRef<ThinkingSection[] | null>(null);

  // Apply-request listeners (parent hook subscribes for full_edit / quote_edit handling)
  const applyListenersRef = useRef<Set<(req: ApplyRequest) => void>>(new Set());

  const onApplyRequest = useCallback((handler: (req: ApplyRequest) => void) => {
    applyListenersRef.current.add(handler);
    return () => {
      applyListenersRef.current.delete(handler);
    };
  }, []);

  const emitApply = useCallback((req: ApplyRequest) => {
    for (const h of applyListenersRef.current) {
      try {
        h(req);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useChat] apply handler threw', err);
      }
    }
  }, []);

  // ---------------- message helpers ----------------

  const appendUserMessage = useCallback((text: string): string => {
    const id = uuid();
    setMessages((m) => [...m, { id, role: 'human', text, createdAt: Date.now() }]);
    return id;
  }, []);

  const appendAiMessage = useCallback(
    (patch: Partial<ChatMessage> = {}): string => {
      const id = patch.id ?? uuid();
      const msg: ChatMessage = {
        id,
        role: 'llm',
        text: '',
        createdAt: Date.now(),
        streaming: true,
        ...patch,
      };
      // If thinking arrived before send-handler called appendAiMessage,
      // drain the queue into this new slot.
      if (pendingThinkingRef.current && !msg.thinking) {
        msg.thinking = pendingThinkingRef.current;
        pendingThinkingRef.current = null;
      }
      setMessages((m) => [...m, msg]);
      setStreamingId(id);
      return id;
    },
    [setStreamingId],
  );

  const updateMessage = useCallback((id: string, patch: Partial<ChatMessage>) => {
    setMessages((m) => m.map((msg) => (msg.id === id ? { ...msg, ...patch } : msg)));
  }, []);

  const startResearchThread = useCallback((): string => {
    const id = uuid();
    setCurrentThreadId(id);
    return id;
  }, [setCurrentThreadId]);

  const startEditThread = useCallback((): string => {
    const id = uuid();
    setCurrentThreadId(id);
    return id;
  }, [setCurrentThreadId]);

  const stop = useCallback(async () => {
    const threadId = currentThreadIdRef.current;
    if (!threadId) return;
    try {
      await apiClient.post('/api/v1/chat/stop', { chat_id: threadId });
    } catch {
      /* ignore — reconcile on next SSE event */
    }
  }, []);

  const reset = useCallback(() => {
    setMessages([]);
    setStreamingId(null);
    setResearchStatusSafe(null);
    setCurrentThreadId(null);
    setLoadedChatType(null);
    seenEventKeysRef.current.clear();
    pendingThinkingRef.current = null;
  }, [setStreamingId, setResearchStatusSafe, setCurrentThreadId]);

  const loadThread = useCallback(
    (threadId: string, hydratedMessages: ChatMessage[], chatType?: LoadedChatType) => {
      // Swap the active thread and messages in one render pass.
      seenEventKeysRef.current.clear();
      pendingThinkingRef.current = null;
      setMessages(hydratedMessages);
      setStreamingId(null);
      setResearchStatusSafe(null);
      setCurrentThreadId(threadId);
      setLoadedChatType(chatType ?? null);
    },
    [setStreamingId, setResearchStatusSafe, setCurrentThreadId],
  );

  // ---------------- SSE routing ----------------

  const processSseEvent = useCallback(
    (event: SseEvent) => {
      const threadId = event.thread_id;
      if (!threadId || threadId !== currentThreadIdRef.current) return;

      // Dedup: SSE reconnects / StrictMode double-dispatch can replay events.
      // We include event.timestamp (Backend stamps `time.Now().Unix()` per
      // delivery — see unified_consumer.go:675) and event.correlation_id as
      // PRIMARY discriminators so two semantically-identical responses in
      // the same thread (e.g., the canned guardrail rejection for "Hi"
      // repeated twice) no longer collide and silently drop the second
      // event. Content fields stay in the key so SSE reconnects that
      // genuinely replay the SAME event are still caught.
      const key = [
        event.thread_id,
        event.feature_id ?? '',
        event.correlation_id ?? '',
        event.timestamp ?? '',
        event.type ?? '',
        event.sub_type ?? '',
        event.status ?? '',
        event.metadata?.research_status?.phase ?? '',
        event.metadata?.research_status?.state ?? '',
        event.metadata?.research_status?.label ?? '',
        event.metadata?.edit_status?.step ?? '',
        event.metadata?.edit_status?.message ?? '',
        (event.response ?? '').length,
        (event.response ?? '').slice(0, 120),
      ].join('|');
      if (seenEventKeysRef.current.has(key)) return;
      seenEventKeysRef.current.add(key);

      // Backend-reported terminal error
      if (event.status === 'failed' || event.status === 'error') {
        const aiId = streamingIdRef.current;
        if (aiId) {
          updateMessage(aiId, {
            streaming: false,
            error: event.response || 'The agent reported an error.',
          });
        }
        setStreamingId(null);
        setResearchStatusSafe(null);
        return;
      }

      // ----- Chat name auto-generation (web app treats silently) -----
      if (event.sub_type === 'name_update') return;
      if (event.sub_type === 'cancellation_ack') {
        setStreamingId(null);
        setResearchStatusSafe(null);
        return;
      }

      // ----- Edit flows -----
      const isDraftType = event.type === 'draft' || event.type === 'draft_chat';

      // quote_edit response: AI returned edited text for a specific selection
      if (isDraftType && event.metadata?.edited_text && event.metadata?.query_type === 'quote_edit') {
        const cleaned = stripCodeFences(event.metadata.edited_text);
        const changeId = String(event.metadata.change_id ?? '') || uuid();
        const aiId = streamingIdRef.current;
        if (aiId) {
          updateMessage(aiId, {
            text: event.response ?? `Proposed edit for the selected text.`,
            role: 'change',
            changeId,
            streaming: false,
            citations: normalizeCitations(event.metadata.citations?.citations),
          });
          emitApply({ kind: 'quote_edit', messageId: aiId, editedText: cleaned, changeId });
        }
        setStreamingId(null);
        setResearchStatusSafe(null);
        return;
      }

      // full_edit response: AI wrote a new DOCX to S3
      if (isDraftType && event.metadata?.s3_key && event.response && event.should_show !== false) {
        const aiId = streamingIdRef.current ?? appendAiMessage({ text: event.response });
        updateMessage(aiId, {
          text: event.response,
          editedS3Key: event.metadata.s3_key,
          editCount:
            typeof event.metadata.edit_count === 'number' ? event.metadata.edit_count : undefined,
          streaming: true, // keep streaming until applier completes
          citations: event.metadata.citations?.citations ?? [],
        });
        // The apply handler downloads the server's edited DOCX from
        // s3_key and splices in its w:ins/w:del tracked changes via
        // applyEditedDocument. We no longer parse metadata.edit_operations
        // — the server's edits live in the DOCX itself, and the per-edit
        // review cards read them straight from the document.
        emitApply({
          kind: 'full_edit',
          messageId: aiId,
          s3Key: event.metadata.s3_key,
          editCount:
            typeof event.metadata.edit_count === 'number' ? event.metadata.edit_count : undefined,
          latestVersion:
            typeof event.metadata.latest_version === 'string'
              ? event.metadata.latest_version
              : undefined,
          docxSha256:
            typeof event.metadata.docx_sha256 === 'string'
              ? event.metadata.docx_sha256
              : undefined,
        });
        // streamingId stays set until applier finishes; researchStatus clears
        setResearchStatusSafe(null);
        return;
      }

      // Edit progress status (analyzing/understanding/editing) — mirror to
      // research-style thinking panel for consistency.
      if (isDraftType && event.metadata?.edit_status) {
        const step = event.metadata.edit_status.step;
        const msg = event.metadata.edit_status.message ?? '';
        const phaseMap: Record<string, ResearchPhase> = {
          analyzing: 'decomposition',
          understanding: 'search',
          editing: 'compilation',
        };
        const phase = step ? phaseMap[step] ?? null : null;
        setResearchStatusSafe({
          phase,
          state: 'in_progress',
          label: msg,
        });
      }

      // ----- Research / chat flows -----
      // Research status updates accumulate thinking sections and drive the progress panel.
      if (event.metadata?.research_status) {
        const normalized = normalizeResearchStatus(event.metadata.research_status);
        setResearchStatusSafe(normalized);
        synthesizeThinking(normalized);
      }

      // Explicit thinking array from backend (passes through when research_status absent)
      if (event.metadata?.thinking && Array.isArray(event.metadata.thinking)) {
        patchCurrentSlotThinking(event.metadata.thinking);
      }

      // Terminal research answer
      const isResearchType =
        event.type === 'research' ||
        event.type === 'research_chat' ||
        event.type === 'quick_research' ||
        event.type === 'deep_research' ||
        event.type === 'llm_direct' ||
        event.type === 'ai_response' ||
        event.type === 'ai_response_chat';

      if (
        isResearchType &&
        event.response &&
        event.should_show !== false &&
        !event.metadata?.research_status // terminal means no more progress updates
      ) {
        const citations: Citation[] = normalizeCitations(event.metadata?.citations?.citations);
        const aiId = streamingIdRef.current ?? appendAiMessage({ text: event.response });
        updateMessage(aiId, {
          text: event.response,
          citations,
          streaming: false,
        });
        setStreamingId(null);
        setResearchStatusSafe(null);
      }

      // Internal helpers closed over refs
      function patchCurrentSlotThinking(sections: ThinkingSection[]) {
        const aiId = streamingIdRef.current;
        if (!aiId) {
          // No slot yet — buffer for next appendAiMessage to pick up.
          pendingThinkingRef.current = sections;
          return;
        }
        updateMessage(aiId, { thinking: sections });
      }

      function synthesizeThinking(status: ResearchStatus) {
        const aiId = streamingIdRef.current;
        if (!aiId) {
          // If slot isn't created yet, the thinking synthesized here will be
          // applied when appendAiMessage runs next (via pendingThinkingRef).
          pendingThinkingRef.current = buildSynthesizedThinking(status, pendingThinkingRef.current);
          return;
        }
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== aiId) return m;
            const next = buildSynthesizedThinking(status, m.thinking ?? []);
            return { ...m, thinking: next };
          }),
        );
      }
    },
    [appendAiMessage, emitApply, setResearchStatusSafe, setStreamingId, updateMessage],
  );

  useSSE({ onEvent: processSseEvent });

  return useMemo(
    () => ({
      mode,
      setMode,
      messages,
      streamingId,
      researchStatus,
      currentThreadId,
      loadedChatType,
      startResearchThread,
      startEditThread,
      setCurrentThreadId,
      appendUserMessage,
      appendAiMessage,
      updateMessage,
      loadThread,
      reset,
      stop,
      onApplyRequest,
    }),
    [
      mode,
      messages,
      streamingId,
      researchStatus,
      currentThreadId,
      loadedChatType,
      startResearchThread,
      startEditThread,
      setCurrentThreadId,
      appendUserMessage,
      appendAiMessage,
      updateMessage,
      loadThread,
      reset,
      stop,
      onApplyRequest,
    ],
  );
}

// ---------- pure helpers ----------

/**
 * Backend citations use snake_case (pdf_path, chunk_id, relevance_score).
 * Our Citation type is camelCase. Normalize here so downstream code doesn't
 * need to sniff both shapes.
 */
export function normalizeCitations(raw: unknown): Citation[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r, i): Citation => {
    const c = (r ?? {}) as Record<string, unknown>;
    return {
      id: String(c.id ?? c.chunk_id ?? c.chunkId ?? `cit-${i}`),
      source: (c.source as string) ?? undefined,
      title: (c.title as string) ?? undefined,
      url: (c.url as string) ?? undefined,
      chunkId: (c.chunk_id as string) ?? (c.chunkId as string) ?? undefined,
      content: (c.content as string) ?? (c.text as string) ?? undefined,
      relevanceScore:
        typeof c.relevance_score === 'number'
          ? (c.relevance_score as number)
          : typeof c.relevanceScore === 'number'
            ? (c.relevanceScore as number)
            : undefined,
      pdfPath: (c.pdf_path as string) ?? (c.pdfPath as string) ?? undefined,
      page: typeof c.page === 'number' ? (c.page as number) : undefined,
    };
  });
}

function stripCodeFences(s: string): string {
  return s
    .trim()
    .replace(/^\s*```[a-zA-Z0-9_-]*\s*/, '')
    .replace(/\s*```\s*$/, '')
    .trim();
}

function normalizeResearchStatus(raw: RawResearchStatus): ResearchStatus {
  const phase = (raw.phase ?? null) as ResearchStatus['phase'];
  const state = (raw.state ?? null) as ResearchStatus['state'];
  return {
    phase,
    state,
    label: String(raw.label ?? ''),
    subQueries: raw.sub_queries,
    counters: raw.counters,
    highConfidenceHits: raw.high_confidence_hits,
    keySources: raw.key_sources,
    timingMs: raw.timing_ms,
  };
}

/**
 * Derive thinking sections from the running research status. Mirrors the
 * web app's setResearchStatus reducer (researchSlice.ts:714-791): for each
 * phase we create a titled section. Subsequent updates for the same phase
 * replace the section in place.
 */
function buildSynthesizedThinking(
  status: ResearchStatus,
  existing: ThinkingSection[] | null,
): ThinkingSection[] {
  const next = Array.isArray(existing) ? [...existing] : [];
  const upsert = (title: string, content: string) => {
    const idx = next.findIndex((s) => s.title === title);
    if (idx >= 0) next[idx] = { title, content };
    else next.push({ title, content });
  };

  switch (status.phase) {
    case 'decomposition': {
      const queries = status.subQueries ?? [];
      const content =
        queries.length > 0
          ? `Identified ${queries.length} research sub-quer${queries.length === 1 ? 'y' : 'ies'}:\n\n` +
            queries
              .map(
                (sq: ResearchSubQuery, i: number) =>
                  `${i + 1}. ${sq.query}${sq.rationale ? `\n   Rationale: ${sq.rationale}` : ''}`,
              )
              .join('\n\n')
          : status.label || 'Decomposing query into research sub-tasks…';
      upsert('Query Decomposition', content);
      break;
    }
    case 'search': {
      const c = status.counters ?? {};
      const done = c.sub_queries_done ?? 0;
      const total = c.sub_queries_total ?? 0;
      const dbCits = c.db_citations ?? 0;
      const webCits = c.web_citations ?? 0;
      let content = `Searching databases and the web.\n\n`;
      if (total) content += `Progress: ${done}/${total} sub-queries complete.\n`;
      if (dbCits || webCits) content += `Found ${dbCits} database result${plural(dbCits)} and ${webCits} web result${plural(webCits)}.\n`;
      const hits = status.highConfidenceHits ?? [];
      if (hits.length) {
        content += `\nHigh-confidence matches:\n`;
        content += hits
          .slice(0, 5)
          .map((h) => `- ${h.title ?? 'Untitled'}${h.source ? ` (${h.source})` : ''}`)
          .join('\n');
      }
      upsert('Database & Web Search', content.trim());
      break;
    }
    case 'verification': {
      const content =
        status.state === 'completed'
          ? 'Cross-referenced findings against statutes and verified citations. All sources validated.'
          : status.state === 'failed'
            ? 'Verification failed — insufficient relevant results found.'
            : status.label || 'Verifying retrieved sources against the query…';
      upsert('Verification', content);
      break;
    }
    case 'compilation': {
      const content =
        status.state === 'completed'
          ? 'Synthesized findings from all sources into a comprehensive answer with proper citations.'
          : status.label || 'Compiling final answer…';
      upsert('Compilation', content);
      break;
    }
    default:
      break;
  }
  return next;
}

function plural(n: number): string {
  return n === 1 ? '' : 's';
}
